import type { NavItem, NavSection } from '../config/nav';

/**
 * Ctrl+K quick jump (Cmd+K on macOS; Warren, 21 October 2026, item M: "Ctrl+K quick jump"). The pages it lists are the
 * person's own menu, so it can never offer a page the menu would not; it changes no one's menu. A 24 character id pasted
 * in opens a job, PO, store order or charger, but only through the Api's existing scoped lookups, which answer 404 or 403
 * for anything the person may not see. Pure: the dialog (components/ui/nav/QuickJump.tsx) only wires these to the page.
 */
export interface JumpPage { kind: 'page'; id: string; label: string; section: string; href: string }
export type JumpType = 'job' | 'po' | 'order' | 'charger';
export interface JumpItem { kind: 'item'; type: JumpType; label: string; href: string }
export type JumpOption = JumpPage | JumpItem;

/** "Available to every persona whose menu has more than 5 leaves." */
export const QUICK_JUMP_MIN_PAGES = 6;

/** Every page in the menu, once per address, in menu order, with the section it sits in. */
export function jumpPages(sections: ReadonlyArray<Pick<NavSection, 'label' | 'items'>>): JumpPage[] {
  const out: JumpPage[] = [], seen = new Set<string>();
  const walk = (section: string, items: ReadonlyArray<NavItem>) => {
    for (const i of items) {
      if (i.kind === 'parent') { walk(section, i.children); continue; }
      if (seen.has(i.href)) continue;
      seen.add(i.href);
      out.push({ kind: 'page', id: i.id, label: i.label, section, href: i.href });
    }
  };
  sections.forEach(s => walk(s.label, s.items));
  return out;
}

export const quickJumpEnabled = (pages: readonly JumpPage[]) => pages.length >= QUICK_JUMP_MIN_PAGES;

/**
 * The pages matching what is typed: every word must appear in the page's name or its section. Names that start with
 * the text come first, then a word in the name that starts with it, then the rest, each in menu order.
 */
export function filterJumpPages(pages: readonly JumpPage[], query: string): JumpPage[] {
  const q = query.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!q) return [...pages];
  const words = q.split(' ');
  const rank = (p: JumpPage) => {
    const label = p.label.toLowerCase();
    if (label.startsWith(q)) return 0;
    if (label.split(/[\s&-]+/).some(w => w.startsWith(q))) return 1;
    if (label.includes(q)) return 2;
    return 3;
  };
  return pages
    .map((p, order) => ({ p, order, rank: rank(p) }))
    .filter(({ p }) => { const text = `${p.label} ${p.section}`.toLowerCase(); return words.every(w => text.includes(w)); })
    .sort((a, b) => a.rank - b.rank || a.order - b.order)
    .map(x => x.p);
}

type ShortcutEvent = { key: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean; shiftKey: boolean; defaultPrevented?: boolean };
/** Ctrl+K, or Cmd+K on a Mac; never with Alt or Shift, and never a key something else already handled. */
export function isQuickJumpShortcut(e: ShortcutEvent, mac: boolean): boolean {
  if (e.defaultPrevented || e.altKey || e.shiftKey || e.key.toLowerCase() !== 'k') return false;
  return mac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
}

export type JumpKey = { type: 'move'; index: number } | { type: 'go'; index: number } | { type: 'close' } | { type: 'none' };
/** The dialog's keys: arrows move (and wrap), Home and End jump, Enter goes, Escape closes. */
export function jumpKey(key: string, index: number, count: number): JumpKey {
  if (key === 'Escape') return { type: 'close' };
  if (!count) return { type: 'none' };
  const at = Math.min(Math.max(index, 0), count - 1);
  switch (key) {
    case 'ArrowDown': return { type: 'move', index: (at + 1) % count };
    case 'ArrowUp': return { type: 'move', index: (at - 1 + count) % count };
    case 'Home': return { type: 'move', index: 0 };
    case 'End': return { type: 'move', index: count - 1 };
    case 'Enter': return { type: 'go', index: at };
    default: return { type: 'none' };
  }
}

/** A 24 character id pasted in, or null. */
export function pastedId(query: string): string | null {
  const t = query.trim();
  return /^[a-f0-9]{24}$/i.test(t) ? t.toLowerCase() : null;
}

/**
 * The id lookups, each tried only when the person's own menu has the page it opens. Each reuses the Api's existing
 * scoped read of one item (GET /admin/partner-jobs/:id, /procurement/:id, /admin/orders/:id, /chargers/:id); a refusal
 * (404 outside scope, 403 without the permission) is no result.
 */
export const JUMP_TYPES: ReadonlyArray<{ type: JumpType; menu: readonly string[]; href: (id: string) => string }> = [
  { type: 'job', menu: ['/sales-jobs'], href: id => `/sales-jobs/${id}` },
  { type: 'po', menu: ['/procurement'], href: id => `/procurement/${id}` },
  // Store orders are a tab of Money; their own page is /orders/:id.
  { type: 'order', menu: ['/money'], href: id => `/orders/${id}` },
  // One Chargers page since 28 October 2026; its whole network scope (/chargers?scope=network) is the same page.
  { type: 'charger', menu: ['/chargers'], href: id => `/chargers/${id}` },
];
/** One item's name if the Api lets the person see it; throws (or returns null) if not. */
export type JumpLookup = (id: string) => Promise<string | null>;

export async function lookupPastedId(id: string, pages: readonly JumpPage[], lookups: Partial<Record<JumpType, JumpLookup>>): Promise<JumpItem[]> {
  const menu = new Set(pages.map(p => p.href));
  const tried = JUMP_TYPES.filter(t => lookups[t.type] && t.menu.some(h => menu.has(h)));
  const answers = await Promise.allSettled(tried.map(t => lookups[t.type]!(id)));
  return tried.flatMap((t, i) => {
    const a = answers[i]!;
    return a.status === 'fulfilled' && a.value ? [{ kind: 'item' as const, type: t.type, label: a.value, href: t.href(id) }] : [];
  });
}
